import { useId } from 'react';

import { Button } from '../../shared/ui/Button';
import { Dialog, DialogBody, DialogFooter, DialogHeader } from '../../shared/ui/Dialog';
import { StatusBanner } from '../../shared/ui/StatusBanner';

import s from './ArchiveStudentDialog.module.css';

interface Props {
  /** The student to archive, or null when the dialog is closed. */
  studentName: string | null;
  open: boolean;
  pending: boolean;
  error: string | null;
  onConfirm: () => void;
  onClose: () => void;
}

export function ArchiveStudentDialog({
  studentName,
  open,
  pending,
  error,
  onConfirm,
  onClose,
}: Props): JSX.Element {
  const titleId = useId();
  const name = studentName ?? 'this student';

  return (
    <Dialog open={open} onClose={onClose} labelledById={titleId} size="sm">
      <DialogHeader
        titleId={titleId}
        title={`Archive ${name}?`}
        subtitle="They’ll be taken off the active roster — but nothing is deleted."
        onClose={onClose}
      />
      <DialogBody>
        <ul className={s.list}>
          <li className={s.removed}>
            <span className={s.bullet} aria-hidden="true" />
            Removed from all batches and any upcoming exams
          </li>
          <li className={s.kept}>
            <span className={s.bullet} aria-hidden="true" />
            Their past mock attempts and results stay on record
          </li>
          <li className={s.kept}>
            <span className={s.bullet} aria-hidden="true" />
            Reversible — restore anytime (you’ll re-add them to batches)
          </li>
        </ul>

        {error !== null ? (
          <div className={s.banner}>
            <StatusBanner tone="error">{error}</StatusBanner>
          </div>
        ) : null}
      </DialogBody>
      <DialogFooter>
        <Button type="button" variant="ghost" onClick={onClose} disabled={pending}>
          Cancel
        </Button>
        <Button type="button" variant="danger" loading={pending} onClick={onConfirm}>
          Archive student
        </Button>
      </DialogFooter>
    </Dialog>
  );
}
